import { useCallback, useEffect, useState } from 'react';
import { gql } from 'graphql-tag';
import { useMutation, useApolloClient } from '@apollo/client';
import { useIntl } from 'react-intl';
import { syncedResourceAllocationFragment } from '~/modules/resourcing/common/fragments';
import { useBackgroundJobStatus } from '~/modules/common/hooks/useBackgroundJobStatus';
import { BACKGROUND_JOB_STATUS } from '~/modules/common/enums/BackgroundJobStatus';
import { useSnackbar } from '~/modules/common/snackbar';
import {
  appendScheduleRuleTypeNameFields,
  mergeFragmentCache
} from '~/modules/resourcing/common/util';
import { useResourceUserTaskAssignmentBulkAction } from './useResourceUserTaskAssignmentBulkAction';

const BULK_UNASSIGN_TASKS_TO_RESOURCE_USER = gql`
  mutation bulkUnassignTasksToResourceUser(
    $input: BulkUnassignTasksToResourceUserInput!
  ) {
    bulkUnassignTasksToResourceUser(input: $input) {
      failedTaskIds
      syncedResourceAllocation {
        ...SyncedResourceAllocation
      }
      isResourceAllocationSynced
    }
  }
  ${syncedResourceAllocationFragment}
`;

export const useBulkUnassignTasksSubmit = ({
  projectId,
  resource,
  textSearch,
  resetTaskAssignments,
  onTaskAssignmentUpdateSuccess,
  onClose,
  isRmpTaskAllocationEnhancement1
}) => {
  const { cache } = useApolloClient();
  const { showSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const [
    bulkUnassignTasksToResourceUser,
    { loading: isBulkUnassignTasksInProgress }
  ] = useMutation(BULK_UNASSIGN_TASKS_TO_RESOURCE_USER);

  const handleBulkUnassignTasksSubmitLegacy = useCallback(async () => {
    const {
      data: {
        bulkUnassignTasksToResourceUser: {
          isResourceAllocationSynced,
          syncedResourceAllocation
        }
      }
    } = await bulkUnassignTasksToResourceUser({
      variables: {
        input: {
          projectId,
          resourceUserId: resource.id,
          textSearch
        }
      }
    });

    onTaskAssignmentUpdateSuccess({
      userId: resource.id,
      isResourceAllocationDeleted:
        isResourceAllocationSynced && !syncedResourceAllocation
    });
    onClose();
    resetTaskAssignments();
  }, [
    bulkUnassignTasksToResourceUser,
    projectId,
    resource.id,
    textSearch,
    resetTaskAssignments,
    onTaskAssignmentUpdateSuccess,
    onClose
  ]);

  const [jobId, setJobId] = useState(null);
  const { status, hasProcessed, result } = useBackgroundJobStatus({
    jobId
  });

  const {
    isBulkActionInProgress,
    handleBulkUnassignTasksSubmit
  } = useResourceUserTaskAssignmentBulkAction({
    projectId,
    resource,
    textSearch
  });

  const handleJobSuccess = useCallback(() => {
    const { isResourceAllocationSynced, syncedResourceAllocation } =
      result?.data || {};

    onTaskAssignmentUpdateSuccess({
      userId: resource.id,
      isResourceAllocationDeleted:
        isResourceAllocationSynced && !syncedResourceAllocation
    });

    if (isResourceAllocationSynced && syncedResourceAllocation) {
      const updatedSyncedResourceAllocation = {
        ...syncedResourceAllocation,
        scheduleRules: syncedResourceAllocation.scheduleRules.map(
          appendScheduleRuleTypeNameFields
        )
      };

      mergeFragmentCache({
        cache,
        fragment: syncedResourceAllocationFragment,
        __typename: 'ResourceAllocation',
        newData: updatedSyncedResourceAllocation
      });
    }

    showSnackbar &&
      showSnackbar(
        formatMessage({
          id:
            'taskAssignmentBulkActionDialog.removeAllTaskAssignmentsSnackbarSuccessMessage'
        })
      );

    onClose();
    resetTaskAssignments();
  }, [
    cache,
    result?.data,
    resource.id,
    onTaskAssignmentUpdateSuccess,
    onClose,
    resetTaskAssignments,
    showSnackbar,
    formatMessage
  ]);

  useEffect(() => {
    if (
      isRmpTaskAllocationEnhancement1 &&
      jobId &&
      hasProcessed &&
      status === BACKGROUND_JOB_STATUS.SUCCEEDED
    ) {
      handleJobSuccess();
    }
  }, [
    isRmpTaskAllocationEnhancement1,
    jobId,
    hasProcessed,
    status,
    handleJobSuccess
  ]);

  const handleSubmit = useCallback(async () => {
    if (isRmpTaskAllocationEnhancement1) {
      const newJobId = await handleBulkUnassignTasksSubmit();

      setJobId(newJobId);
    } else {
      await handleBulkUnassignTasksSubmitLegacy();
    }
  }, [
    isRmpTaskAllocationEnhancement1,
    handleBulkUnassignTasksSubmit,
    handleBulkUnassignTasksSubmitLegacy
  ]);

  return {
    isBulkUnassignTasksInProgress: isRmpTaskAllocationEnhancement1
      ? isBulkActionInProgress || (jobId && !hasProcessed)
      : isBulkUnassignTasksInProgress,
    handleBulkUnassignTasksSubmit: handleSubmit
  };
};
